import * as Sentry from '@sentry/react';
import { bifrostEnv } from '../config/config';
import type { HTTPMethod } from './fetchHelper';

const EXPECTED_AUTH_HTTP_FAILURE_PROD_SAMPLE_RATE = 0.01;

// Report every expected auth failure outside prod to make staging/dev smoke tests deterministic.
// In prod, sample these high-volume 401/403 events so Sentry keeps auth visibility without noise.
const shouldReportExpectedAuthHttpFailure = (): boolean =>
	bifrostEnv !== 'prod' || Math.random() < EXPECTED_AUTH_HTTP_FAILURE_PROD_SAMPLE_RATE;

const stripQueryAndHash = (url: string): string => url.split(/[?#]/)[0];

const getRequestPath = (url: string): string => {
	try {
		return new URL(url, window.location.origin).pathname;
	} catch {
		return stripQueryAndHash(url);
	}
};

export const reportExpectedAuthHttpFailure = ({
	method,
	statusCode,
	url,
}: {
	method: HTTPMethod;
	statusCode: 401 | 403;
	url: string;
}): void => {
	if (!shouldReportExpectedAuthHttpFailure()) {
		return;
	}

	const requestPath = getRequestPath(url);
	const serializedStatusCode = String(statusCode);

	Sentry.captureMessage('Expected auth HTTP failure', {
		level: 'info',
		fingerprint: ['expected-auth-http-failure', method, requestPath, serializedStatusCode],
		tags: {
			expectedAuthFailure: 'true',
			requestMethod: method,
			requestPath,
			statusCode: serializedStatusCode,
		},
		contexts: {
			authHttpFailure: {
				method,
				path: requestPath,
				statusCode: serializedStatusCode,
			},
		},
	});
};
